import { useEffect, useState, useRef } from "react";
// import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import { Axios } from "api/axios";
import WarehouseAutocomplete from "Common/Components/InputComponent/WarehouseAutocomplete";
import ActionButton from "components/Buttons/ActionButton";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import ThemeTabs from "components/Tabs/ThemeTabs";
import { app_modules } from "helpers/apps_modules_list";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchWarehouseDropdown } from "slices/Master/Warehouse/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import NoWarehousesMessage from "Common/Components/NoWarehousesMessage";
import IssueToLabour from "./IssueToLabour/List";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import Dispatch from "./Dispatch/List";
import StockTransfer from "./StockTransfer/List";
import { api } from "helpers/api_url";

const DispatchContainer = () => {
  const dispatch = useDispatch<any>();
  const location = useLocation();
  const prevTabRef = useRef<number | null>(null);
  const [tab, setTab] = useState(
    Number(sessionStorage?.getItem("InventoryDispachTab")) || 1
  );

  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // 🔥 Use preserved table state hook with tabIndex for per-tab state preservation
  const tableState = usePreservedTableState({
    key: "inventoryDispatch",
    tabIndex: tab,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection: "" | "asc" | "desc" | undefined = sorting[0]?.desc
    ? "desc"
    : sorting[0]
    ? "asc"
    : "";

  // 🔥 Warehouse search (optional) - stored separately as it's not part of table state
  const [warehouseIdSearch, setWarehouseIdSearch] = useState("");

  // Use custom hook to fetch and access permissions
  const { canView } = usePermission(
    app_modules?.inventory?.app_name,
    app_modules?.inventory?.modules?.dispatch
  );
  // const warehouse_permissions = user_permissions?.find(
  //   (dispatch: any) => dispatch?.apps_name === app_modules?.warehouse?.app_name
  // );
  // const dispatch_permissions = warehouse_permissions?.modules_permissions?.find(
  //   (dispatch: any) =>
  //     dispatch?.modules_name === app_modules?.warehouse?.modules?.dispatch
  // );

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // ✅ Tab switch - reset state when tab changes
  const handleTabChange = (val: number) => {
    setTab(val);
    sessionStorage.setItem("InventoryDispachTab", val.toString());
  };

  // Reset state when tab changes (using useEffect to handle tab changes)
  // This ensures state is cleared when switching tabs, but preserved when navigating away and back
  useEffect(() => {
    // Only reset if tab actually changed (not on initial mount)
    // The hook will have re-initialized with the new tabIndex by this point
    if (prevTabRef.current !== null && prevTabRef.current !== tab) {
      // Clear the state for the new tab
      resetState();
    }
    prevTabRef.current = tab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]); // Only depend on tab, not resetState to avoid infinite loops

  // Tabs configuration
  const tabs = [
    { label: "Dispatch" },
    { label: "Stock Transfer" },
    { label: "Issue to Labour" },
  ];

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      let exportApi = "";
      let filename = "";

      switch (tab) {
        // case 1:
        //   exportApi = api.warehouse.dispatch.ready_for_dispatch.export_csv;
        //   filename = "ready_for_dispatch.csv";
        //   break;

        case 1:
          exportApi = api.Inventory.dispatch.dispatch_done.export_csv;
          filename = "dispatch.csv";
          break;

        case 2:
          exportApi = api.Inventory.dispatch.stock_transfer.export_csv;
          filename = "stock_transfer.csv";
          break;

        case 3:
          exportApi = api.Inventory.dispatch.issue_to_labour.export_csv;

          filename = "issue_to_labour.csv";
          break;

        default:
          showCustomSwal({
            icon: "info",
            title: "Invalid Tab",
            text: "Invalid tab selected.",
          });
          return;
      }

      // Build params EXACTLY like thunk
      const params = new URLSearchParams();

      // NO pagination for export

      // Search
      if (search) params.append("search", search);

      // Sorting
      if (sortField) {
        const orderingValue =
          sortDirection === "desc" ? `-${sortField}` : sortField;
        params.append("ordering", orderingValue);
      }

      // Warehouse
      if (warehouseIdSearch) {
        // if (tab === 2) {
          params.append("warehouse_id", warehouseIdSearch);
        // } else {
        //   params.append("dispatch_from_warehouse_id", warehouseIdSearch);
        // }
      }

      //  EXACT FILTER LOGIC LIKE YOUR THUNK
      if (filterValue?.length) {
        filterValue.forEach((f: any) => {
          const { filterField, filterValue } = f;

          if (!filterField || filterValue === undefined || filterValue === null)
            return;

          // 🗓 Date Range
          if (typeof filterValue === "object" && filterValue !== null) {
            const { from_date, to_date } = filterValue;

            const formatDate = (date: any) => {
              if (!date) return "";
              return new Date(date).toISOString().split("T")[0];
            };

            if(filterField==="dispatch_date" || filterField==="created_at" || filterField==="updated_at") {
             params.append(`${filterField}_from`, formatDate(from_date));
             params.append(`${filterField}_to`, formatDate(to_date));
            }
            return;
          }

          //  Normal filter
          params.append(filterField, String(filterValue));
        });
      }

      // Force CSV Response
      params.append("export", "csv");

      const response = await Axios.get(`${exportApi}?${params.toString()}`, {
        responseType: "blob",
      });

      // Download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      console.error("Export error:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export CSV.";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };



  useEffect(() => {
    const fetchInit = async () => {
      try {
        setLoading(true);
        const { payload } = await dispatch(fetchWarehouseDropdown());
        setWarehouseOptions(payload.data);

        // Check if stored warehouse is still valid (exists in current options)
        const storedWarehouse = sessionStorage.getItem("InventoryIdDispach");
        const isStoredWarehouseValid = storedWarehouse &&
          payload.data.some((warehouse: any) => warehouse.warehouse_id === storedWarehouse);

        if (storedWarehouse && isStoredWarehouseValid) {
          setWarehouseIdSearch(storedWarehouse);
        } else {
          // Clear invalid stored warehouse and auto-select first available
          sessionStorage.removeItem("InventoryIdDispach");
          setWarehouseIdSearch(payload.data[0]?.warehouse_id || "");
          sessionStorage.setItem(
            "InventoryIdDispach",
            payload.data[0]?.warehouse_id || ""
          );
        }
      } catch (error) {
        toast.error("Failed to fetch initial data");
      } finally {
        setLoading(false);
      }
    };
    fetchInit();
  }, [dispatch]);

  // ✅ Debounce search input to actual search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  return (
    <ListingPageContainer>
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                {/* Title */}
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={2}
                  xl={2}
                  className="mb-2 mb-lg-0"
                >
                  <h6 className="card-title mb-0">Dispatch</h6>
                </Col>

                {/* Right-aligned elements container */}
                <Col
                  xs={12}
                  sm={12}
                  md={12}
                  lg={10}
                  xl={10}
                  className="mb-2 mb-lg-0"
                >
                  <Row className="g-2 justify-content-end align-items-center">
                    {/* Search Input Box */}
                    <Col xs={12} sm={12} md={6} lg={3} xl={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    {/* Warehouse Autocomplete */}
                    <Col
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                      xl={4}
                      className=""
                      style={{ minWidth: "200px" }}
                    >
                      <WarehouseAutocomplete
                        options={warehouseOptions}
                        value={warehouseIdSearch}
                        loading={loading}
                        showWarehouseType={true}
                        disableClearable
                        sessionStorageKey="InventoryIdDispach"
                        onChange={(warehouseId) => {
                          setWarehouseIdSearch(String(warehouseId));

                          // Reset filters and sorting when warehouse changes
                          setColumnFilters([]);
                          setFilterValue([]);
                          setSorting([]);
                          setPageIndex(0);
                        }}
                      />
                    </Col>

                    {/* Buttons */}
                    <Col
                      xs={12}
                      sm={12}
                      md={12}
                      lg="auto"
                      xl="auto"
                      className="d-flex gap-2 align-items-center flex-wrap flex-lg-nowrap justify-content-start justify-content-lg-start"
                    >
                      {canView && (
                        <ActionButton
                          variant="light"
                          label="Export"
                          icon="bi bi-box-arrow-up"
                          handleClick={handleExport}
                        />
                      )}

                      {/* {dispatch_permissions?.permissions?.Create === true && (
                        <>
                          <ActionButton
                            label="Issue to Labour"
                            icon="bi bi-plus-circle"
                            navigateLink="/warehouse-dispatch/IssueToLabour/create-dispatch-labour"
                            variant="primary"
                          />
                          <ActionButton
                            label="Create Stock Transfer"
                            icon="bi bi-plus-circle"
                            navigateLink="/warehouse-dispatch/stockTransfer/create-stock-transfer"
                            variant="primary"
                          />
                        </>
                      )} */}
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="mt-3">
                {/* Tabs */}
                <Col xs={12}>
                  <ThemeTabs
                    tabs={tabs}
                    value={tab - 1}
                    onChange={(newValue) => handleTabChange(newValue + 1)}
                  />
                </Col>
              </Row>
            </Card.Header>

            <Card.Body>
              {warehouseOptions.length > 0 ? (
                <LoaderErrorWrapper loading={loading} error={null}>
                  {tab === 1 && (
                    <Dispatch
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                  {tab === 2 && (
                    <StockTransfer
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                  {tab === 3 && (
                    <IssueToLabour
                      loading={loading}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      sorting={sorting}
                      columnFilters={columnFilters}
                      filterValue={filterValue}
                      search={search}
                      searchInput={searchInput}
                      sortField={sortField}
                      sortDirection={sortDirection}
                      warehouseIdSearch={warehouseIdSearch}
                      setPageIndex={setPageIndex}
                      setPageSize={setPageSize}
                      setSorting={setSorting}
                      setColumnFilters={setColumnFilters}
                      setFilterValue={setFilterValue}
                      setSearch={setSearch}
                      setSearchInput={setSearchInput}
                      setWarehouseIdSearch={setWarehouseIdSearch}
                    />
                  )}
                </LoaderErrorWrapper>
              ) : (
                <NoWarehousesMessage />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default DispatchContainer;
